/**
 * Amplify configuration for CDK-provisioned Cognito User Pool.
 *
 * Pattern 4 from RESEARCH.md: Wire @aws-amplify/ui-react to an existing
 * CDK-provisioned Cognito User Pool without Amplify backend.
 *
 * IMPORTANT: No identityPoolId — it is NOT required for SPA + API Gateway
 * JWT auth (Anti-Pattern from RESEARCH.md). Including it causes unnecessary
 * IAM credential flows.
 *
 * All env vars must be VITE_-prefixed (Pitfall 9 from RESEARCH.md).
 * Values come from apps/web/.env.local (produced by CDK stack outputs).
 */
import { Amplify } from 'aws-amplify';
// REQUIRED for the Hosted-UI (Google) callback to complete in the PRODUCTION build
// (debug session google-sso-login-blank, 2026-07-09). The OAuth code→token exchange
// runs via a listener that Amplify registers only as an import side-effect of
// signInWithRedirect — which lives in the lazily-loaded login/Authenticator chunk.
// On /login?code=...&state=..., fetchAuthSession() blocks on waitForInflightOAuth()
// until that exchange completes, and the route guard awaits fetchAuthSession before
// loading the login chunk → deadlock: blank screen, exchange never starts. This
// explicit import registers the listener in the entry chunk at boot (Amplify's
// documented fix for code-split/multi-page apps). Dev is unaffected either way —
// Vite dev doesn't split the chunk, which is why this only failed in deployed builds.
import 'aws-amplify/auth/enable-oauth-listener';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { isRunningInNativeShell } from './pwa';

// Cognito Hosted-UI domain for social sign-in (Google). Public, non-secret.
const oauthDomain =
  (import.meta.env.VITE_OAUTH_DOMAIN as string) || 'auth.sandbox.app.brokeornot.com';

// Must match the Cognito app client's callback/logout URLs. IMPORTANT: Amplify v6 selects the
// redirect_uri by finding the entry that startsWith `window.location.origin + window.location.pathname`,
// falling back to the FIRST entry if none match. Social sign-in is initiated from the /login page, so
// these MUST be the /login URLs — root URLs never match from /login, and Amplify would fall back to
// localhost, causing Cognito `redirect_mismatch`. Superset across environments is fine — each env's
// Cognito client only whitelists its own origin (see EnvConfig.oauthRedirects).
const oauthRedirects = [
  'http://localhost:5173/login',
  'https://sandbox.brokeornot.com/login',
  'https://sandbox.app.brokeornot.com/login',
  'https://app.brokeornot.com/login',
];

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID as string,
      // identityPoolId is intentionally omitted (see Anti-Patterns in RESEARCH.md)
      loginWith: {
        oauth: {
          domain: oauthDomain,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: oauthRedirects,
          redirectSignOut: oauthRedirects,
          responseType: 'code',
        },
      },
    },
  },
});

// CAP-04: on native, persist Cognito tokens in Keychain/Keystore-backed secure storage
// instead of WebView localStorage, so sessions survive force-quit. This is Amplify's
// documented KeyValueStorageInterface substitution point — must run right after
// Amplify.configure, before any Amplify auth usage (same ordering constraint as the
// OAuth listener import above). Web path is untouched: default storage stays in effect.
//
// Exported (rather than inlined) because lib/nativeGoogleAuth.ts constructs its own
// DefaultTokenStore to write the tokens it obtains out-of-band from the system-browser
// OAuth flow. That store must read/write the SAME storage Amplify was configured with,
// or the tokens would land somewhere fetchAuthSession() never looks.
type AmplifyKeyValueStorage = Parameters<typeof cognitoUserPoolsTokenProvider.setKeyValueStorage>[0];

export const nativeTokenStorage: AmplifyKeyValueStorage | undefined = isRunningInNativeShell()
  ? {
      getItem: (key) => SecureStorage.getItem(key),
      setItem: (key, value) => SecureStorage.setItem(key, value),
      removeItem: (key) => SecureStorage.removeItem(key),
      clear: () => SecureStorage.clear(),
    }
  : undefined;

if (nativeTokenStorage) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(nativeTokenStorage);
}
